import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useData } from '../../contexts/DataContext';
import MobileLayout from '../layout/MobileLayout';
import MobileBottomNav from '../layout/MobileBottomNav';
import { LoadingSpinner } from '../common/LoadingSpinner';
import Button from '../common/Button';
import ShareSheet from '../common/ShareSheet';
import TaskCompletionModal from './TaskCompletionModal';
import AddTaskModal from './AddTaskModal';
import EditTaskModal from './EditTaskModal';
import FileViewerModal from '../files/FileViewerModal';
import FollowUpTasksModal from './FollowUpTasksModal';
import { DetailPaneShell } from '../common/DetailPaneShell';
import { toast } from 'sonner';
import { confirmAction } from '../../utils/confirmDialog';
import { useLanguage } from '../../contexts/LanguageContext';
import { isTaskOverdue } from '../../utils/dateUtils';
import { PaperPlaneTilt } from '@phosphor-icons/react';
import { MessageContactModal } from './MessageContactModal';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { TaskMessageHistory } from './TaskMessageHistory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaskDetailsPage = ({ taskId: paneTaskId, embedded = false, onClose, onBack, backLabel, onSelectTask, onOpenContact, onChanged }) => {
  const params = useParams();
  const taskId = paneTaskId || params.taskId;
  const navigate = useNavigate();
  const { t: translate } = useLanguage();
  const tr = (key, fallback) => (translate(key) === key ? fallback : translate(key));
  const goToTask = (id) => (embedded ? onSelectTask(id) : navigate(`/tasks/${id}`));
  const finishAndLeave = (path) => {
    if (embedded) {
      onChanged?.();
      onClose?.();
    } else {
      navigate(path);
    }
  };
  const { t } = useLanguage();
  const { archiveTask, restoreTask, deleteTask } = useData();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [contact, setContact] = useState(null);
  const [showMessageContact, setShowMessageContact] = useState(false);
  const isDesktop = useIsDesktop();
  const { activeWorkspace } = useWorkspace() || {};
  const canMessageContact = isDesktop && activeWorkspace?.kind === 'org';
  const [messageHistoryKey, setMessageHistoryKey] = useState(0);
  const [followUpTasks, setFollowUpTasks] = useState([]);
  const [recurringTasks, setRecurringTasks] = useState([]);
  // Share sheet (popover on desktop, bottom sheet on mobile).
  const [shareOpen, setShareOpen] = useState(false);
  const shareBtnRef = useRef(null);
  const [showFollowUpTasksModal, setShowFollowUpTasksModal] = useState(false);

  useEffect(() => {
    fetchTask();
  }, [taskId]);

  const fetchTask = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API}/tasks/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setTask(response.data);
      
      // Fetch contact details if task has a contact (non-blocking)
      if (response.data.contact_id) {
        fetchContact(response.data.contact_id);
      }
      
      // Fetch follow-up tasks (non-blocking)
      fetchFollowUpTasks(response.data.id);
      
      // Automatically fetch recurring tasks if this is part of a recurring group
      if (response.data.recurring || response.data.parent_recurring_task_id) {
        const recurringGroupId = response.data.parent_recurring_task_id || response.data.id;
        console.log('🔄 Task is part of recurring group:', {
          taskId: response.data.id,
          taskTitle: response.data.title,
          isRecurring: response.data.recurring,
          parentRecurringTaskId: response.data.parent_recurring_task_id,
          recurringGroupId
        });
        fetchRecurringTasks(recurringGroupId);
      } else {
        console.log('❌ Task is not part of a recurring group:', {
          taskId: response.data.id,
          taskTitle: response.data.title,
          isRecurring: response.data.recurring,
          parentRecurringTaskId: response.data.parent_recurring_task_id
        });
      }
      
    } catch (error) {
      console.error('Error fetching task:', error);
      if (error.response?.status === 404) {
        setError('Task not found');
      } else if (error.response?.status === 403) {
        setError('Access denied');
      } else {
        setError('Failed to load task details');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchContact = async (contactId) => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setContact(response.data);
    } catch (error) {
      console.error('Error fetching contact:', error);
      // Don't set error for contact fetch failure, just leave contact as null
    }
  };

  const fetchFollowUpTasks = async (parentTaskId) => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Filter tasks that have this task as parent
      const followUps = response.data.filter(task => task.parent_task_id === parentTaskId);
      setFollowUpTasks(followUps);
    } catch (error) {
      console.error('Error fetching follow-up tasks:', error);
      // Don't set error, just leave followUpTasks as empty array
    }
  };

  const fetchRecurringTasks = async (recurringGroupId) => {
    try {
      console.log('🔄 Fetching recurring tasks for group ID:', recurringGroupId);
      
      const response = await axios.get(`${API}/tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          all: true // Get all tasks to find the recurring group
        }
      });
      
      console.log('📋 All tasks received:', response.data.length);
      console.log('🔍 Looking for tasks with parent_recurring_task_id or id matching:', recurringGroupId);
      
      // Filter tasks that are part of the same recurring group
      const recurringGroupTasks = response.data.filter(task => {
        const isInGroup = task.parent_recurring_task_id === recurringGroupId || 
                         (task.id === recurringGroupId && task.recurring);
        
        if (isInGroup) {
          console.log('✅ Found recurring task:', {
            id: task.id,
            title: task.title,
            recurring: task.recurring,
            parent_recurring_task_id: task.parent_recurring_task_id
          });
        }
        
        return isInGroup;
      });
      
      console.log(`🔄 Found ${recurringGroupTasks.length} tasks in recurring group`);
      
      // Sort with the original recurring task (the one with task.recurring = true) first
      recurringGroupTasks.sort((a, b) => {
        // Original recurring task first
        if (a.recurring && !b.recurring) return -1;
        if (!a.recurring && b.recurring) return 1;
        
        // Then sort by creation date (earliest first)
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      });
      
      console.log('🎯 Final recurring tasks list:', recurringGroupTasks.map(t => ({
        id: t.id,
        title: t.title,
        recurring: t.recurring
      })));
      
      setRecurringTasks(recurringGroupTasks);
    } catch (error) {
      console.error('❌ Error fetching recurring tasks:', error);
      // Don't set error, just leave recurringTasks as empty array
    }
  };

  const handleBack = () => {
    // Smart navigation: always go back to tasks list (parent page)
    // Use replace to avoid adding to history stack
    if (embedded) onClose?.();
    else navigate('/', { replace: true });
  };

  const renderLayout = (children, rightAction) => (embedded ? (
    <DetailPaneShell
      title={t('taskDetails')}
      onClose={onClose}
      onBack={onBack}
      backLabel={backLabel || tr('backToContact', 'Back to contact')}
      rightAction={rightAction}
    >
      {children}
    </DetailPaneShell>
  ) : (
    <MobileLayout title={t('taskDetails')} showBack onBack={handleBack} rightAction={rightAction}>{children}</MobileLayout>
  ));

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    fetchTask(); // Refresh task data after edit
    onChanged?.();
  };

  // -----------------------------------------------------------------
  // Sharing — fetch the file from the API with auth, then trigger a
  // browser download / copy-to-clipboard. Backend endpoints are defined
  // in /app/backend/sharing.py.
  // -----------------------------------------------------------------
  const downloadFromApi = async (path, fallbackName) => {
    const resp = await fetch(`${API}${path}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!resp.ok) throw new Error(`Server responded ${resp.status}`);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const handleShareIcs = async () => {
    try {
      const safe = (task?.title || 'task').replace(/[^\w-]+/g, '_');
      await downloadFromApi(`/tasks/${taskId}/share/ics`, `${safe}.ics`);
    } catch (err) {
      console.error('ICS share failed:', err);
      alert('Could not generate calendar invite.');
    }
  };

  const handleCopyShareLink = async () => {
    try {
      const resp = await fetch(`${API}/tasks/${taskId}/share-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await resp.json();
      if (!data?.url) throw new Error('No url returned');
      try {
        await navigator.clipboard.writeText(data.url);
        alert(t('linkCopied') || 'Link copied to clipboard');
      } catch (_e) {
        // clipboard write blocked → fall back to prompt so the user can copy manually
        window.prompt(t('copyShareLink') || 'Copy share link', data.url);
      }
    } catch (err) {
      console.error('Share link failed:', err);
      alert(t('linkCopyFailed') || 'Could not copy link');
    }
  };

  const handleArchive = async () => {
    if (!(await confirmAction({
      title: tr('archiveThisTaskQ', 'Archive this task?'),
      message: tr('archiveTaskHint', 'You can restore it any time from Archives.'),
      confirmLabel: tr('archive', 'Archive'),
    }))) {
      return;
    }

    try {
      await archiveTask(taskId);
      toast.success(t('taskArchivedSuccessfully') || 'Task archived successfully');
      finishAndLeave('/');
    } catch (error) {
      console.error('Error archiving task:', error);
      toast.error('Failed to archive task');
    }
  };

  const handleRestore = async () => {
    if (!(await confirmAction({
      title: tr('restoreThisTaskQ', 'Restore this task?'),
      message: tr('restoreTaskHint', 'It will move back to your active tasks.'),
      confirmLabel: tr('restore', 'Restore'),
    }))) {
      return;
    }

    try {
      await restoreTask([taskId]);
      toast.success(t('taskRestoredSuccessfully') || 'Task restored successfully');
      finishAndLeave('/archives');
    } catch (error) {
      console.error('Error restoring task:', error);
      toast.error('Failed to restore task');
    }
  };

  const handleDelete = async () => {
    if (!(await confirmAction({
      title: tr('deleteThisTaskQ', 'Delete this task?'),
      message: tr('deleteTaskHint', 'This cannot be undone.'),
      confirmLabel: tr('delete', 'Delete'),
      destructive: true,
    }))) {
      return;
    }

    try {
      await deleteTask(taskId);
      toast.success(tr('taskDeletedShort', 'Task deleted'));
      finishAndLeave('/');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleCompleteTask = () => {
    setShowCompletionModal(true);
  };

  const handleCompletionSuccess = () => {
    setShowCompletionModal(false);
    fetchTask(); // Refresh task data to show completion details
    onChanged?.();
  };

  const handleCreateFollowUp = () => {
    setShowFollowUpModal(true);
  };

  const handleFollowUpSuccess = () => {
    setShowFollowUpModal(false);
    fetchFollowUpTasks(taskId);
    onChanged?.();
  };

  const handleRecurringClick = () => {
    // Scroll to recurring tasks section instead of navigating away
    const recurringSection = document.getElementById('recurring-tasks-section');
    if (recurringSection) {
      recurringSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFileClick = (file) => {
    console.log('🔍 File clicked - Full object:', file);
    console.log('🔍 File properties:', {
      filename: file.filename,
      id: file.id,
      content_type: file.content_type,
      size_mb: file.size_mb,
      has_content: !!file.content
    });
    
    // Add task_id to the file object for the FileViewerModal
    const fileWithTaskId = {
      ...file,
      task_id: taskId // Add the current task ID
    };
    
    setCurrentFile(fileWithTaskId);
    setShowFileViewer(true);
  };

  if (loading) {
    return renderLayout(
      <>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
        {!embedded && <MobileBottomNav />}
      </>
    );
  }

  if (error || !task) {
    return renderLayout(
      <>
        <div className="mobile-card p-8 text-center">
          <div className="text-6xl mb-4">😞</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('taskNotFound')}</h3>
          <p className="text-gray-600 mb-4">{error || t('taskNotFoundDesc')}</p>
          <Button onClick={handleBack}>
            Go Back
          </Button>
        </div>
        {!embedded && <MobileBottomNav />}
      </>
    );
  }

  const priorityColors = {
    [t('high')]: 'bg-red-100 text-red-800',
    [t('medium')]: 'bg-yellow-100 text-yellow-800',
    [t('low')]: 'bg-green-100 text-green-800'
  };

  const headerActions = (
        <div className="flex items-center space-x-2 relative">
          {/* Share button — opens the ShareSheet (popover on desktop, sheet on mobile) */}
          <button
            ref={shareBtnRef}
            onClick={() => setShareOpen((v) => !v)}
            className="text-white hover:text-gray-200 lg:text-gray-600 lg:hover:text-blue-600 transition-colors p-1"
            title={t('share') || 'Share'}
            aria-label={t('share') || 'Share'}
            data-testid="task-share-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </button>
          <ShareSheet
            open={shareOpen}
            onClose={() => setShareOpen(false)}
            anchorRef={shareBtnRef}
            title={t('shareThisTask') || 'Share this task'}
            options={[
              {
                id: 'ics',
                icon: '📅',
                label: t('sendAsCalendarInvite') || 'Send as calendar invite',
                description: t('sendAsCalendarInviteDesc') || 'Opens in Google, Outlook or Apple Calendar',
                onClick: handleShareIcs,
              },
              {
                id: 'link',
                icon: '🔗',
                label: t('copyShareLink') || 'Copy share link',
                description: t('copyShareLinkDesc') || 'Anyone with the link can view, read-only',
                onClick: handleCopyShareLink,
              },
            ]}
          />
          {task.archived ? (
            // Show Restore button for archived tasks
            <button
              onClick={handleRestore}
              className="text-white hover:text-gray-200 lg:text-gray-600 lg:hover:text-blue-600 transition-colors p-1"
              title={t('restoreTask') || 'Restore'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          ) : task.completed ? (
            // Show Archive button for completed (non-archived) tasks
            <button
              onClick={handleArchive}
              className="text-white hover:text-gray-200 lg:text-gray-600 lg:hover:text-blue-600 transition-colors p-1"
              title={t('archiveTask')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </button>
          ) : (
            // Show Edit button for active tasks
            <button
              onClick={handleEdit}
              className="text-white hover:text-gray-200 lg:text-gray-600 lg:hover:text-blue-600 transition-colors p-1"
              title={t('edit')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
          <button
            onClick={handleDelete}
            className="text-white hover:text-red-200 lg:text-gray-600 lg:hover:text-red-600 transition-colors p-1"
            title={t('delete')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
  );

  return renderLayout(
    <>
      <div className="lg:max-w-5xl lg:mx-auto">
      {/* Task Header — priority-coloured top accent (red/amber/emerald) per
          the design guidelines. Anchors the page visually without changing
          the body structure. */}
      <div
        className={`bg-white rounded-2xl border border-gray-200 border-t-4 ${
          task.priority === 'High' ? 'border-t-red-500'
            : task.priority === 'Medium' ? 'border-t-amber-500'
            : 'border-t-emerald-500'
        } p-6 mb-4 shadow-sm`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="font-display text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight">
                {task.title}
              </h1>
              
              {/* Follow-up Tasks Indicator */}
              {followUpTasks.length > 0 && (
                <button
                  onClick={() => setShowFollowUpTasksModal(true)}
                  className="flex items-center space-x-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-200 transition-colors"
                  title={`${t('thisTaskHasFollowUps')} ${followUpTasks.length} follow-up task${followUpTasks.length !== 1 ? 's' : ''}`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  <span>{followUpTasks.length} {followUpTasks.length === 1 ? (t('followUp') === 'followUp' ? 'follow-up' : t('followUp')) : t('followUps')}</span>
                </button>
              )}
            </div>
          </div>
          {task.completed && (
            <div className="ml-3 flex-shrink-0">
              <span className="text-green-600 text-3xl">✓</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${priorityColors[task.priority] || 'bg-gray-100 text-gray-800'}`}>
            {task.priority} {t('priorityLabel')}
          </span>
          
          {task.category && (
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {task.category}
            </span>
          )}
          
          {(task.recurring || task.parent_recurring_task_id) && (
            <button 
              onClick={handleRecurringClick}
              className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium hover:bg-purple-200 transition-colors cursor-pointer"
              title={t('jumpToRecurringSection')}
            >
              🔄 {t('recurring')}
            </button>
          )}
          
          {task.completed && (
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              {t('completedLabel')}
            </span>
          )}
        </div>

        {/* Task Actions */}
        {!task.completed ? (
          <div className="space-y-3">
            <div className="flex space-x-3">
              <Button
                onClick={handleCompleteTask}
                className="flex-1"
              >
                {t('markComplete')}
              </Button>
            </div>
            
            {/* Completion Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-2">
                  <svg className="w-4 h-4 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-sm text-blue-800 leading-relaxed">
                  {t('completionInfo')}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex space-x-3">
              <Button
                onClick={handleCreateFollowUp}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
              >
                {t('createFollowUpTask')}
              </Button>
            </div>
            
            {/* Follow-up Task Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-2">
                  <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-sm text-green-800 leading-relaxed">
                  {t('followUpTaskInfo')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Task Details */}
      <div className="mobile-card p-6 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
        
        <div className="space-y-4">
          {task.due_date && (
            <div className="flex items-center">
              <span className="text-gray-600 w-20">Due Date:</span>
              <span className="font-medium">
                {new Date(task.due_date).toLocaleDateString()}
                {task.time && ` at ${task.time}`}
              </span>
            </div>
          )}
          
          {task.location && (
            <div className="flex items-center">
              <span className="text-gray-600 w-20">Location:</span>
              <span className="font-medium">{task.location}</span>
            </div>
          )}
          
          {task.contact_id && (
            <div className="flex items-center">
              <span className="text-gray-600 w-20">Contact:</span>
              <button
                onClick={() => (embedded ? onOpenContact(task.contact_id) : navigate(`/contacts/${task.contact_id}`))}
                data-testid="task-details-contact-link"
                className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
              >
                {contact ? contact.name : `Contact #${task.contact_id}`}
              </button>
            </div>
          )}
          
          <div className="flex items-start">
            <span className="text-gray-600 w-20">Created:</span>
            <span className="font-medium">
              {new Date(task.created_at).toLocaleDateString()}
            </span>
          </div>
          
          {task.completed_at && (
            <div className="flex items-start">
              <span className="text-gray-600 w-24 flex-shrink-0">Completed:</span>
              <span className="font-medium text-green-600">
                {new Date(task.completed_at).toLocaleDateString()}
              </span>
            </div>
          )}

          {/* Message contact — shown at the bottom of the details list so it
              doesn't crowd the contact name row on narrow mobile screens. */}
          {canMessageContact && task.contact_id && contact && (
            <div className="pt-1">
              <button
                onClick={() => setShowMessageContact(true)}
                data-testid="task-details-message-contact-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
              >
                <PaperPlaneTilt size={14} weight="bold" />Message contact
              </button>
            </div>
          )}
          
          {/* Reminder Status — mobile only */}
          {!isDesktop && (
          <div className="flex items-center pt-4 border-t border-gray-200" data-testid="task-details-reminder-status">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="reminder-status"
                checked={task.reminder_enabled || false}
                disabled
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-not-allowed opacity-60"
              />
              <label htmlFor="reminder-status" className="ml-3">
                <span className="font-medium text-gray-900">⏰ {t('enableReminder') || 'Reminder'}</span>
                {task.reminder_enabled && (
                  <p className="text-sm text-gray-600 mt-1">
                    {task.reminder_sent 
                      ? '✅ Reminder sent' 
                      : '⏳ Reminder will be sent 1 hour before due time'}
                  </p>
                )}
                {!task.reminder_enabled && (
                  <p className="text-sm text-gray-600 mt-1">
                    No reminder set for this task
                  </p>
                )}
              </label>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Notes */}
      {task.notes && (
        <div className="mobile-card p-6 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
          <p className="text-gray-700 leading-relaxed">{task.notes}</p>
        </div>
      )}

      <TaskMessageHistory taskId={task.id} refreshKey={messageHistoryKey} />

      {/* Task Attachments (excluding completion attachments) */}
      {task.attachments && task.attachments.filter(att => att.file_type !== 'completion').length > 0 && (
        <div className="mobile-card p-6 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Task Attachments ({task.attachments.filter(att => att.file_type !== 'completion').length})
          </h2>
          <div className="space-y-3">
            {task.attachments.filter(att => att.file_type !== 'completion').map((attachment, index) => (
              <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{attachment.filename}</h3>
                  <p className="text-sm text-gray-500">
                    {attachment.content_type} • {attachment.size_mb} MB
                  </p>
                </div>
                <button 
                  className="ml-3 text-blue-600 hover:text-blue-800"
                  onClick={() => handleFileClick(attachment)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completion Details - MOVED TO BOTTOM */}
      {task.completed && (task.completion_notes || task.completed_at || (task.attachments && task.attachments.some(att => att.file_type === 'completion'))) && (
        <div className="mobile-card p-6 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Completion Details</h2>
          
          <div className="space-y-4">
            {task.completed_at && (
              <div className="flex items-start">
                <span className="text-gray-600 w-24">Completed:</span>
                <span className="font-medium text-green-600">
                  {new Date(task.completed_at).toLocaleDateString()} at {new Date(task.completed_at).toLocaleTimeString()}
                </span>
              </div>
            )}
            
            {task.completion_notes && (
              <div>
                <span className="text-gray-600 font-medium">Notes:</span>
                <p className="text-gray-700 mt-1 leading-relaxed bg-green-50 p-3 rounded-lg">
                  {task.completion_notes}
                </p>
              </div>
            )}

            {/* Completion Attachments */}
            {task.attachments && task.attachments.filter(att => att.file_type === 'completion').length > 0 && (
              <div>
                <span className="text-gray-600 font-medium">Attachments:</span>
                <div className="mt-2 space-y-2">
                  {task.attachments.filter(att => att.file_type === 'completion').map((attachment, index) => (
                    <div key={index} className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex-shrink-0 mr-3">
                        {attachment.content_type?.startsWith('image/') ? '🖼️' :
                         attachment.content_type?.startsWith('audio/') ? '🎵' :
                         attachment.content_type?.includes('pdf') ? '📄' :
                         attachment.content_type?.includes('document') || attachment.content_type?.includes('word') ? '📝' : '📎'}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{attachment.filename}</h4>
                        <p className="text-sm text-gray-600">
                          {attachment.content_type} • {attachment.size_mb ? `${attachment.size_mb} MB` : 'Unknown size'}
                        </p>
                      </div>
                      <button 
                        className="ml-3 text-green-600 hover:text-green-800 transition-colors"
                        onClick={() => handleFileClick(attachment)}
                        title="View/Download attachment"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Follow-up Tasks */}
      {followUpTasks.length > 0 && (
        <div className="mobile-card p-6 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Follow-up Tasks ({followUpTasks.length})
          </h2>
          <div className="space-y-3">
            {followUpTasks.map((followUpTask) => (
              <div key={followUpTask.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <button
                      onClick={() => goToTask(followUpTask.id)}
                      className="text-left hover:text-blue-600 transition-colors"
                    >
                      <h3 className="font-medium text-gray-900 hover:text-blue-600">
                        {followUpTask.title}
                      </h3>
                      {followUpTask.due_date && (
                        <p className="text-sm text-gray-500 mt-1">
                          Due: {new Date(followUpTask.due_date).toLocaleDateString()}
                        </p>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 ml-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      followUpTask.completed 
                        ? 'bg-green-100 text-green-800' 
                        : isTaskOverdue(followUpTask)
                        ? 'bg-red-100 text-red-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {followUpTask.completed ? 'Completed' : 
                       isTaskOverdue(followUpTask) ? 'Overdue' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recurring Tasks */}
      {recurringTasks.length > 0 && (
        <div id="recurring-tasks-section" className="mobile-card p-6 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            🔄 Recurring Tasks ({recurringTasks.length})
          </h2>
          <div className="space-y-3">
            {recurringTasks.map((recurringTask, index) => (
              <div key={recurringTask.id} className="p-3 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <button
                      onClick={() => goToTask(recurringTask.id)}
                      className="text-left hover:text-purple-600 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900 hover:text-purple-600">
                          {recurringTask.title}
                        </h3>
                        {recurringTask.recurring && (
                          <span className="px-2 py-0.5 bg-purple-200 text-purple-800 rounded text-xs font-medium">
                            Original
                          </span>
                        )}
                      </div>
                      {recurringTask.due_date && (
                        <p className="text-sm text-gray-500 mt-1">
                          Due: {new Date(recurringTask.due_date).toLocaleDateString()}
                        </p>
                      )}
                      {recurringTask.recurring_frequency && (
                        <p className="text-xs text-purple-600 mt-1">
                          Repeats: {recurringTask.recurring_frequency}
                        </p>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 ml-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      recurringTask.completed 
                        ? 'bg-green-100 text-green-800' 
                        : isTaskOverdue(recurringTask)
                        ? 'bg-red-100 text-red-800'
                        : recurringTask.id === task.id
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {recurringTask.completed ? 'Completed' : 
                       isTaskOverdue(recurringTask) ? 'Overdue' :
                       recurringTask.id === task.id ? 'Current' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!embedded && <MobileBottomNav />}

      </div>{/* end lg:max-w-4xl wrapper */}

      {/* Task Completion Modal */}
      <TaskCompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        task={task}
        onComplete={handleCompletionSuccess}
      />

      {/* Edit Task Modal */}
      {showEditModal && (
        <EditTaskModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          task={task}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Follow-Up Task Modal */}
      {showFollowUpModal && (
        <AddTaskModal
          onClose={() => setShowFollowUpModal(false)}
          onSuccess={handleFollowUpSuccess}
          initialData={{
            title: `Follow-up: ${task.title}`,
            category: task.category,
            contact_id: task.contact_id,
            parent_task_id: task.id,
            priority: 'Medium'
          }}
        />
      )}

      {/* File Viewer Modal */}
      {showFileViewer && currentFile && (
        <FileViewerModal
          isOpen={showFileViewer}
          onClose={() => setShowFileViewer(false)}
          file={currentFile}
        />
      )}

      {/* Follow-Up Tasks Modal */}
      <FollowUpTasksModal
        isOpen={showFollowUpTasksModal}
        onClose={() => setShowFollowUpTasksModal(false)}
        parentTask={task}
      />

      {showMessageContact && contact && (
        <MessageContactModal
          task={task}
          contact={contact}
          onClose={() => setShowMessageContact(false)}
          onSent={() => setMessageHistoryKey((k) => k + 1)}
        />
      )}

    </>,
    headerActions
  );
};

export default TaskDetailsPage;